import { useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, ExternalLink, PartyPopper, Rocket } from "lucide-react";
import { useTrips, parseCSV, parseCSVRow } from "@/hooks/useTrips";
import { validateEarningsCSV } from "@/lib/csvValidation";
import { useSubscription } from "@/hooks/useSubscription";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import analytics from "@/lib/segment";

/**
 * Full-screen onboarding overlay that forces a single high-value action:
 * importing Turo earnings. Shown to first-time users who have zero trips.
 */
export const EarningsOnboardingOverlay = () => {
  const navigate = useNavigate();
  const { trips, isLoading, importTrips } = useTrips();
  const { vehicleLimit } = useSubscription();
  const [isDragging, setIsDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("onboarding_dismissed") === "1");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasTrips = (trips?.length ?? 0) > 0;

  const handleFiles = useCallback(async (files: FileList) => {
    const csvFiles = Array.from(files).filter(
      (f) => f.type === "text/csv" || f.name.endsWith(".csv")
    );
    if (csvFiles.length === 0) return;

    setValidationError(null);
    setProcessing(true);

    try {
      let allImportData: ReturnType<typeof parseCSVRow>[] = [];

      for (const file of csvFiles) {
        const content = await file.text();
        const { headers, rows } = parseCSV(content);

        const validation = validateEarningsCSV(headers);
        if (!validation.valid) {
          setValidationError(validation.message);
          setProcessing(false);
          return;
        }

        const fileData = rows
          .filter((row) => row.length > 1 && row[0])
          .map((row) => parseCSVRow(headers, row));

        allImportData = [...allImportData, ...fileData];
      }

      if (allImportData.length > 0) {
        await importTrips.mutateAsync({
          importData: allImportData,
          vehicleLimit,
        });

        analytics.track("Onboarding Earnings Imported", {
          trip_count: allImportData.length,
        });

        // Show celebration
        setShowCelebration(true);

        // After 2.5s, navigate to earnings page
        setTimeout(() => {
          navigate("/app/earnings", { replace: true });
        }, 2500);
      }
    } catch (err: any) {
      setValidationError(err?.message || "Failed to import. Please try again.");
    } finally {
      setProcessing(false);
    }
  }, [importTrips, vehicleLimit, navigate]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files) handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) handleFiles(e.target.files);
    },
    [handleFiles]
  );

  // Don't show if still loading or user already has trips
  if (isLoading || hasTrips || dismissed) return null;

  // Celebration screen
  if (showCelebration) {
    return (
      <div className="fixed inset-0 z-[100] bg-background flex items-center justify-center">
        <div className="text-center space-y-4 animate-in fade-in zoom-in duration-500">
          <PartyPopper className="h-16 w-16 text-primary mx-auto" />
          <h1 className="text-3xl font-bold text-foreground">You're all set!</h1>
          <p className="text-muted-foreground text-lg">
            Your earnings are imported. Let's take a look…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="w-full max-w-lg space-y-6">
        {/* Welcome heading */}
        <div className="text-center space-y-2">
          <Rocket className="h-10 w-10 text-primary mx-auto" />
          <h1 className="text-3xl font-bold text-foreground">
            Welcome to HostCaptain!
          </h1>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Let's import your earnings from Turo.{" "}
            <a
              href="https://turo.com/us/en/business/earnings?year=2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline inline-flex items-center gap-1"
            >
              Here's where to download your earnings file
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
            .
            <br />
            <span className="text-sm text-muted-foreground">
              It's under <span className="font-medium">Business → Earnings → Download CSV</span> (click the button on the top right of the screen).
            </span>
            <br />
            <span className="font-medium">It'll take less than 15 seconds.</span>
          </p>
        </div>

        {/* Upload card */}
        <Card>
          <CardContent className="pt-6">
            {validationError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{validationError}</AlertDescription>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setValidationError(null)}
                >
                  Try Again
                </Button>
              </Alert>
            )}

            {!validationError && (
              <div
                onDrop={handleDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                }}
                className={cn(
                  "border-2 border-dashed rounded-lg p-10 text-center transition-colors cursor-pointer",
                  isDragging
                    ? "border-primary bg-primary/5"
                    : "border-muted-foreground/25 hover:border-primary/50",
                  processing && "opacity-50 pointer-events-none"
                )}
                onClick={() => fileInputRef.current?.click()}
              >
                <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">
                  {processing
                    ? "Importing your earnings…"
                    : "Drag and drop your CSV file here, or click to browse"}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  multiple
                  onChange={handleFileInput}
                  className="hidden"
                  disabled={processing}
                />
                <Button variant="outline" disabled={processing} asChild>
                  <span>
                    <Upload className="h-4 w-4 mr-2" />
                    Select File
                  </span>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alternative: no trips yet */}
        <p className="text-center text-sm text-muted-foreground">
          Don't have your first Turo trip yet?{" "}
          <Link
            to="/app/launch-kit"
            onClick={() => { sessionStorage.setItem("onboarding_dismissed", "1"); setDismissed(true); }}
            className="text-primary hover:underline font-medium"
          >
            Start with our beginner guides
          </Link>
        </p>
      </div>
    </div>
  );
};
